import axios from "axios";
import dayjs from "dayjs";
import { EventEmitter } from "events";
import { onValue, ref } from "firebase/database";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { database } from "../firebaseConfig";
import i18n from "../i18n";

const OrderContext = createContext();
const OrderEvents = new EventEmitter();

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [activeStatusStats, setActiveStatusStats] = useState({});
  const [terminalStatusStats, setTerminalStatusStats] = useState({});
  const [statusStatsLoading, setStatusStatsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 200,
    total: 0,
    totalPages: 0,
  });
  const [searchParams, setSearchParams] = useState({
    search: "",
    status: "",
    pickupLocation: "",
    deliveryLocation: "",
    partnerId: "",
    startDate: null, //dayjs(new Date()).format("YYYY-MM-DD"),
    endDate: null, //dayjs(new Date()).format("YYYY-MM-DD"),
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const userInteractedRef = useRef(false);
  const notificationSoundRef = useRef(null);

  // Précharger le son et capturer l'interaction utilisateur
  useEffect(() => {
    const audio = new Audio("/sounds/notif1.mp3");
    audio.preload = "auto";
    notificationSoundRef.current = audio;

    const enableSound = () => {
      userInteractedRef.current = true;
    };
    document.addEventListener("click", enableSound);
    document.addEventListener("keydown", enableSound);
    return () => {
      document.removeEventListener("click", enableSound);
      document.removeEventListener("keydown", enableSound);
    };
  }, []);

  // Update pagination state
  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  // Update search parameters
  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Fetch orders with filters and pagination
  const getOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };
      const response = await axios.get(`/orders`, { params });
      const { data, total, totalPages } = response.data;
      setOrders(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message || error.message;
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [searchParams, pagination.page, pagination.limit]);

  // Status tiles: snapshot for active states + period funnel for terminal states.
  // Period defaults to today when no date filter is set.
  // `status` and `search` are intentionally excluded — they would zero out the
  // tiles by narrowing the dataset to a single status or specific order.
  const getStatusStats = useCallback(async () => {
    try {
      setStatusStatsLoading(true);
      const commonParams = {
        pickupLocation: searchParams.pickupLocation,
        deliveryLocation: searchParams.deliveryLocation,
        partnerId: searchParams.partnerId,
      };
      const today = dayjs().format("YYYY-MM-DD");
      const periodParams = {
        ...commonParams,
        startDate: searchParams.startDate || today,
        endDate: searchParams.endDate || today,
      };
      const [snapshotRes, periodRes] = await Promise.all([
        axios.get(`/stats/orders/snapshot`, { params: commonParams }),
        axios.get(`/stats/orders/period`, { params: periodParams }),
      ]);
      setActiveStatusStats(snapshotRes.data);
      setTerminalStatusStats(periodRes.data.statusStats);
    } catch (error) {
      console.error("Status stats error:", error);
    } finally {
      setStatusStatsLoading(false);
    }
  }, [searchParams]);

  // Fetch a specific order by ID
  const getOrderById = async (orderId) => {
    try {
      const response = await axios.get(`/orders/${orderId}`);
      const order = response.data;
      setOrders((prevData) =>
        prevData.map((data) => (data.id === order.id ? order : data))
      );
      return order;
    } catch (error) {
      console.error("Error fetching order by ID:", error);
      throw error;
    }
  };

  // Update Order Status
  const updateOrderStatus = async (orderId, updatedData) => {
    try {
      const response = await axios.put(
        `/orders/${orderId}/status`,
        updatedData
      );
      const order = response.data;
      setOrders((prevData) =>
        prevData.map((data) => (data.id === order.id ? order : data))
      );
      OrderEvents.emit("order:updated");
      return order;
    } catch (error) {
      console.error("Error updating order status:", error);
      throw error;
    }
  };

  // Update Order Item Status
  const updateOrderItemStatus = async (orderId, orderItemId, updatedData) => {
    try {
      const response = await axios.put(
        `/orders/${orderId}/items/${orderItemId}/status`,
        updatedData
      );
      const order = response.data;
      setOrders((prevData) =>
        prevData.map((data) => (data.id === order.id ? order : data))
      );
      OrderEvents.emit("order:updated");
      return order;
    } catch (error) {
      console.error("Error updating order item status:", error);
      throw error;
    }
  };

  // Dispatch an order to a specific partner (manuel)
  const dispatchOrder = async (orderId, partnerId) => {
    try {
      const response = await axios.put(
        `/orders/${orderId}/dispatch/${partnerId}`
      );
      const order = response.data;
      setOrders((prevData) =>
        prevData.map((data) => (data.id === order.id ? order : data))
      );
      OrderEvents.emit("order:updated");
      return order;
    } catch (error) {
      console.error("Error dispatching order:", error);
      throw error;
    }
  };

  // Auto-dispatch : le backend choisit le partenaire (zone + capacité).
  const autoDispatchOrder = async (orderId) => {
    try {
      const response = await axios.put(`/orders/${orderId}/dispatch`);
      const order = response.data;
      setOrders((prevData) =>
        prevData.map((data) => (data.id === order.id ? order : data))
      );
      OrderEvents.emit("order:updated");
      return order;
    } catch (error) {
      console.error("Error auto-dispatching order:", error);
      throw error;
    }
  };

  // Écoute Firebase en temps réel — scopée par rôle et environnement
  // Filtrage par eventType selon le rôle :
  //   admin  → order.created
  //   partner → order.assigned
  //   client  → order.created
  useEffect(() => {
    const env = localStorage.getItem("apiEnvironment") || "production";
    const userRole = localStorage.getItem("userRole");
    const profileId = localStorage.getItem("profileId");

    let firebasePath;
    let allowedEvent;
    if (userRole === "admin") {
      firebasePath = `events/${env}/global/lastOrderEvent`;
      allowedEvent = "order.created";
    } else if (userRole === "partner" && profileId) {
      firebasePath = `events/${env}/partners/${profileId}/lastOrderEvent`;
      allowedEvent = "order.assigned";
    } else if (userRole === "client" && profileId) {
      firebasePath = `events/${env}/clients/${profileId}/lastOrderEvent`;
      allowedEvent = "order.created";
    } else {
      return;
    }

    const ordersRef = ref(database, firebasePath);

    const unsubscribe = onValue(
      ordersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();

          if (typeof data.timestamp !== "number") {
            return;
          }

          if (lastUpdate !== null && data.timestamp > lastUpdate) {
            setLastUpdate(data.timestamp);

            // Ignorer les events non pertinents pour ce rôle
            if (data.eventType !== allowedEvent) {
              return;
            }

            if (userInteractedRef.current && notificationSoundRef.current) {
              notificationSoundRef.current.currentTime = 0;
              notificationSoundRef.current
                .play()
                .catch((error) => console.error("Erreur audio:", error));
            }

            const message =
              allowedEvent === "order.assigned"
                ? i18n.t("orders.newOrderAssigned")
                : i18n.t("orders.newOrderReceived");

            toast.info(message);
            OrderEvents.emit("order:created");
          } else {
            setLastUpdate(data.timestamp);
          }
        }
      },
      (error) => {
        console.error("Erreur lors de l'écoute Firebase:", error);
      }
    );

    return () => unsubscribe();
  }, [lastUpdate, getOrders]);

  // Context value
  const value = {
    orders,
    activeStatusStats,
    terminalStatusStats,
    statusStatsLoading,
    loading,
    pagination,
    searchParams,
    getOrders,
    getStatusStats,
    getOrderById,
    updateOrderStatus,
    updateOrderItemStatus,
    dispatchOrder,
    autoDispatchOrder,
    updatePagination,
    updateSearchParams,
    OrderEvents,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
